/**
 * stepMeta.tsx
 *
 * Shared step-type and step-status vocabulary for the group processing drawer.
 * Every colour resolves to a design-system CSS custom property — no hex values.
 */

import React from "react";
import {
	ApiOutlined,
	BranchesOutlined,
	ExperimentOutlined,
	FileTextOutlined,
	FlagOutlined,
	RobotOutlined,
	ScissorOutlined,
	UserOutlined,
} from "@ant-design/icons";
import type { ProtocolStep } from "../../../../../../types/lab/ProtocolTypes";

/** Step types the system executes on its own — technicians never open them. */
export const AUTO_STEP_TYPES = new Set(["start", "end", "conditional", "automated"]);

export interface StepTypeCfg {
	label: string;
	accent: string;
	icon: React.ReactNode;
}

export function stepTypeCfg(stepType: string): StepTypeCfg {
	switch (stepType) {
		case "start":
			return { label: "Start", accent: "var(--mg-success)", icon: <FlagOutlined /> };
		case "end":
			return { label: "End", accent: "var(--mg-danger)", icon: <FlagOutlined /> };
		case "conditional":
			return { label: "Conditional", accent: "var(--mg-info)", icon: <BranchesOutlined /> };
		case "automated":
			return { label: "Automated", accent: "var(--mg-text-secondary)", icon: <RobotOutlined /> };
		case "manual":
			return { label: "Manual", accent: "var(--mg-primary)", icon: <UserOutlined /> };
		case "qc":
			return { label: "QC check", accent: "var(--mg-warning)", icon: <ExperimentOutlined /> };
		case "results":
			return { label: "Results", accent: "var(--mg-info)", icon: <FileTextOutlined /> };
		case "aliquot":
			return { label: "Aliquot", accent: "var(--mg-primary)", icon: <ScissorOutlined /> };
		default:
			return { label: stepType, accent: "var(--mg-border-strong)", icon: <ApiOutlined /> };
	}
}

const STATUS_COLORS: Record<string, string> = {
	completed: "var(--mg-success)",
	in_progress: "var(--mg-primary)",
	pending: "var(--mg-text-disabled)",
	failed: "var(--mg-danger)",
	on_hold: "var(--mg-warning)",
	skipped: "var(--mg-info)",
	timed_out: "var(--mg-danger)",
};

export function stepStatusColor(status: string | undefined): string {
	return STATUS_COLORS[status ?? "pending"] ?? STATUS_COLORS.pending;
}

/** Minutes → "1h 20m" / "45m". */
export function formatDuration(minutes: number): string {
	const safe = Math.max(0, Math.round(minutes));
	const hours = Math.floor(safe / 60);
	const mins = safe % 60;
	return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/** Total estimated minutes across a protocol's steps. */
export function totalStepMinutes(steps: ProtocolStep[] | undefined): number {
	return (steps ?? []).reduce((sum, step) => sum + (step.duration_estimate ?? 0), 0);
}
